import React, { useState, useEffect, useMemo } from 'react';
import { Factory, MapPin, Search, ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import FormModalLayout from '../Common/FormModalLayout';
import OutreachShareMenu from '../Common/OutreachShareMenu';
import AuthenticatedBlobImage from '../Common/AuthenticatedBlobImage';
import { buildEnterpriseShareText } from '../../utils/outreachShare';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';
import MapLocationPicker from '../Map/MapLocationPicker';
import { searchAddressNominatim } from '../../utils/geocodeNominatim';
import { useApiQuery } from '../../hooks/useApi';
import { identityService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';
import { useTranslation } from 'react-i18next';
import { bufferPointGeoJSON, parseGeoJSONInput, stringifyGeoJSON } from '../../utils/geoBuffer';
import { formatCnpj } from '../../utils/cnpj';
import { useAuth } from '../../contexts/AuthContext';
import { isRequesterRole } from '../../utils/tenantProfile';

const EnterpriseModal = ({ enterprise, onSubmit, onClose, zIndexClass = 'z-50' }) => {
  const { user } = useAuth();
  const requester = isRequesterRole(user);
  const { t } = useTranslation('enterprises');
  const queryClient = useQueryClient();
  const enterpriseId = enterprise?.id;

  const { data: fotosRaw, refetch: refetchFotos, isLoading: fotosLoading } = useApiQuery(
    ['enterprise-fotos', enterpriseId],
    async () => {
      const r = await identityService.listEnterpriseFotos(enterpriseId);
      const d = r.data;
      return Array.isArray(d) ? d : d?.data ?? [];
    },
    { enabled: !!enterpriseId }
  );
  const fotos = Array.isArray(fotosRaw) ? fotosRaw : [];

  const uploadFotoMutation = useMutation(
    (file) => identityService.uploadEnterpriseFoto(enterpriseId, file),
    {
      onSuccess: () => {
        toast.success('Foto enviada');
        queryClient.invalidateQueries(['enterprise-fotos', enterpriseId]);
        refetchFotos();
      },
      onError: (e) =>
        toast.error(userFacingError(e, 'uploadError')),
    }
  );

  const deleteFotoMutation = useMutation(
    (fotoId) => identityService.deleteEnterpriseFoto(fotoId),
    {
      onSuccess: () => {
        toast.success('Foto removida');
        queryClient.invalidateQueries(['enterprise-fotos', enterpriseId]);
        refetchFotos();
      },
      onError: (e) => toast.error(userFacingError(e, 'deleteError')),
    }
  );

  const handleFotoInput = (ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = '';
    if (!f) return;
    uploadFotoMutation.mutate(f);
  };

  const { data: companiesRaw } = useApiQuery(
    ['identity-companies-select'],
    async () => {
      const r = await identityService.getCompanies({ limit: 500 });
      const d = r.data;
      return Array.isArray(d) ? d : d?.items ?? d?.data ?? [];
    },
    { enabled: !requester }
  );
  const companies = useMemo(
    () => (Array.isArray(companiesRaw) ? companiesRaw : []),
    [companiesRaw]
  );
  const { data: linkedCompany } = useApiQuery(
    ['identity-company', enterprise?.company_id],
    () => identityService.getCompany(enterprise.company_id).then((r) => r.data),
    { enabled: !!enterprise?.company_id }
  );

  const [formData, setFormData] = useState({
    name: '',
    company_id: '',
    cnpj: '',
    status: 'ACTIVE',
    type: 'COMMERCIAL',
    address: '',
    city: '',
    state: '',
    latitude: '',
    longitude: '',
    area_geojson: '',
    inviteEmail: '',
  });
  const [geoQuery, setGeoQuery] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);

  useEffect(() => {
    if (!enterprise) {
      setFormData({
        name: '',
        company_id: '',
        cnpj: '',
        status: 'ACTIVE',
        type: 'COMMERCIAL',
        address: '',
        city: '',
        state: '',
        latitude: '',
        longitude: '',
        area_geojson: '',
        inviteEmail: '',
      });
      return;
    }
    const company =
      linkedCompany || companies.find((c) => String(c.id) === String(enterprise.company_id));
    const rawCnpj = enterprise.cnpj || company?.cnpj || '';
    setFormData({
      name: enterprise.name || enterprise.nome || '',
      company_id: enterprise.company_id || '',
      cnpj: formatCnpj(rawCnpj) || rawCnpj,
      status: enterprise.status || (enterprise.is_active === false ? 'INACTIVE' : 'ACTIVE'),
      type: enterprise.type || 'COMMERCIAL',
      address: enterprise.address || '',
      city: enterprise.city || '',
      state: enterprise.state || '',
      latitude:
        enterprise.latitude != null && enterprise.latitude !== '' ? String(enterprise.latitude) : '',
      longitude:
        enterprise.longitude != null && enterprise.longitude !== '' ? String(enterprise.longitude) : '',
      area_geojson: stringifyGeoJSON(enterprise.area_geojson),
    });
  }, [enterprise, companies, linkedCompany]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const lat =
      formData.latitude === '' ? null : Number(formData.latitude);
    const lng =
      formData.longitude === '' ? null : Number(formData.longitude);
    if (formData.latitude !== '' && Number.isNaN(lat)) {
      toast.error(t('invalidLat'));
      return;
    }
    if (formData.longitude !== '' && Number.isNaN(lng)) {
      toast.error(t('invalidLng'));
      return;
    }
    if (!requester && !formData.company_id && !enterprise) {
      toast.error(t('selectCompany'));
      return;
    }
    const cnpj = formatCnpj(formData.cnpj);
    if (!requester && formData.cnpj.trim() && !cnpj) {
      toast.error('CNPJ incompleto. Informe 14 dígitos ou deixe em branco.');
      return;
    }
    if (!requester && !enterprise && !formData.inviteEmail?.trim()) {
      toast.error(t('inviteEmail'));
      return;
    }
    if (lat == null || lng == null) {
      toast('Sem coordenadas o empreendimento não aparece no mapa.', { icon: '📍' });
    }
    let areaGeo = null;
    try {
      areaGeo = parseGeoJSONInput(formData.area_geojson);
    } catch {
      toast.error('GeoJSON do polígono inválido.');
      return;
    }
    onSubmit({
      name: formData.name.trim(),
      company_id: formData.company_id || enterprise?.company_id || undefined,
      owner_person_id: requester ? user?.person_id : undefined,
      cnpj: cnpj || undefined,
      status: formData.status,
      type: formData.type,
      address: formData.address.trim() || null,
      city: formData.city.trim() || null,
      state: formData.state.trim() || null,
      latitude: lat,
      longitude: lng,
      area_geojson: areaGeo,
      inviteEmail: formData.inviteEmail?.trim(),
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'company_id') {
        const company = companies.find((c) => String(c.id) === String(value));
        if (company?.cnpj) next.cnpj = formatCnpj(company.cnpj);
      }
      if (name === 'cnpj') next.cnpj = formatCnpj(value) || value;
      return next;
    });
  };

  const handleGeocode = async () => {
    if (!geoQuery.trim()) {
      toast.error(t('typeAddress'));
      return;
    }
    setGeoLoading(true);
    try {
      const results = await searchAddressNominatim(geoQuery);
      if (!results.length) {
        toast.error(t('noGeocode'));
        return;
      }
      const top = results[0];
      setFormData((f) => ({
        ...f,
        latitude: String(top.lat),
        longitude: String(top.lon),
      }));
      toast.success('Local definido no mapa');
    } catch (err) {
      console.error(err);
      toast.error(t('geocodeError'));
    } finally {
      setGeoLoading(false);
    }
  };

  const latNum =
    formData.latitude === '' ? null : Number(formData.latitude);
  const lngNum =
    formData.longitude === '' ? null : Number(formData.longitude);

  const enterpriseShareText = useMemo(() => {
    if (!enterprise) return '';
    return buildEnterpriseShareText({
      ...enterprise,
      name: formData.name || enterprise.name || enterprise.nome,
      nome: formData.name || enterprise.nome,
      cnpj: formData.cnpj || enterprise.cnpj,
      type: formData.type || enterprise.type,
      status: formData.status || enterprise.status,
      address: formData.address || enterprise.address,
      city: formData.city || enterprise.city,
      state: formData.state || enterprise.state,
      latitude: formData.latitude === '' ? enterprise.latitude : formData.latitude,
      longitude: formData.longitude === '' ? enterprise.longitude : formData.longitude,
      id: enterprise.id,
    });
  }, [enterprise, formData]);

  const enterpriseShareSubject = useMemo(() => {
    const n = formData.name?.trim() || enterprise?.name || enterprise?.nome || 'Empreendimento';
    return `Empreendimento: ${n}`;
  }, [formData.name, enterprise]);

  return (
    <FormModalLayout
      zIndexClass={zIndexClass}
      title={enterprise ? 'Editar empreendimento' : 'Novo empreendimento'}
      subtitle="Cadastro vinculado a licenças e mapa de empreendimentos."
      headerIcon={Factory}
      historyEntityType="enterprises"
      historyEntityId={enterprise?.id}
      headerExtra={
        enterprise && enterpriseShareText ? (
          <OutreachShareMenu subject={enterpriseShareSubject} text={enterpriseShareText} />
        ) : null
      }
      onClose={onClose}
      size="4xl"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancelar
          </button>
          <Can any={[P.IDENTITY_ACCESS, P.ENTERPRISES_VIEW, P.LICENSE_LICENCAS]}>
            <button type="submit" form="enterprise-modal-form" className="btn btn-primary">
              {enterprise ? 'Atualizar' : 'Criar'}
            </button>
          </Can>
        </>
      }
    >
      <form id="enterprise-modal-form" onSubmit={handleSubmit} className="form-stack">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="form-group md:col-span-2">
            <label htmlFor="ent-name" className="form-label form-label-required">
              Nome do empreendimento
            </label>
            <input
              id="ent-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-input"
              required
            />
          </div>
          {!enterprise && !requester && (
            <div className="form-group md:col-span-2">
              <label htmlFor="ent-company" className="form-label form-label-required">
                Empresa vinculada
              </label>
              <select
                id="ent-company"
                name="company_id"
                value={formData.company_id}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="">Selecione a empresa</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!requester && (
          <div className="form-group">
            <label htmlFor="ent-cnpj" className="form-label">
              CNPJ
            </label>
            <input
              id="ent-cnpj"
              type="text"
              name="cnpj"
              value={formData.cnpj}
              onChange={handleChange}
              className="form-input"
              placeholder="00.000.000/0001-00 (opcional)"
            />
          </div>
          )}
          {!enterprise && !requester && (
            <div className="form-group">
              <label htmlFor="ent-email" className="form-label form-label-required">
                E-mail
              </label>
              <input
                id="ent-email"
                type="email"
                name="inviteEmail"
                value={formData.inviteEmail || ''}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="usuario@empresa.com"
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-gray-500">
                O acesso ao portal é criado automaticamente. A senha provisória e o link de ativação vão para este e-mail.
              </p>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="ent-status" className="form-label">
              Status
            </label>
            <select
              id="ent-status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="form-select"
            >
              <option value="ACTIVE">Ativo</option>
              <option value="INACTIVE">Inativo</option>
            </select>
          </div>
          <div className="form-group md:col-span-2">
            <label htmlFor="ent-type" className="form-label">
              Tipo
            </label>
            <select
              id="ent-type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="form-select"
            >
              <option value="COMMERCIAL">Comercial</option>
              <option value="INDUSTRIAL">Industrial</option>
              <option value="RESIDENTIAL">Residencial</option>
            </select>
          </div>
          <div className="form-group md:col-span-2">
            <label htmlFor="ent-address" className="form-label">
              Endereço
            </label>
            <input
              id="ent-address"
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="ent-city" className="form-label">
              Cidade
            </label>
            <input
              id="ent-city"
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="ent-state" className="form-label">
              UF
            </label>
            <input
              id="ent-state"
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              className="form-input"
              maxLength={2}
              placeholder="SP"
            />
          </div>
        </div>

        <div className="rounded-lg border border-gray-100 bg-slate-50/80 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-800">
            <MapPin className="h-4 w-4 text-primary-600" aria-hidden />
            Localização no mapa
          </div>
          <p className="mb-3 text-xs text-gray-600">
            Busque um endereço ou clique no mapa para definir latitude e longitude. O polígono gerado
            aparece em verde-água sobre o mapa.
          </p>
          {formData.latitude === '' || formData.longitude === '' ? (
            <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Sem latitude e longitude o empreendimento não entra no mapa nem nas sobreposições.
            </p>
          ) : null}
          <div className="mb-3 flex flex-wrap gap-2">
            <input
              type="text"
              className="form-input min-w-[200px] flex-1"
              placeholder="Ex.: Av. Paulista 1000, São Paulo"
              value={geoQuery}
              onChange={(e) => setGeoQuery(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-outline inline-flex items-center gap-2"
              onClick={handleGeocode}
              disabled={geoLoading}
            >
              <Search className="h-4 w-4" />
              {geoLoading ? 'Buscando…' : 'Buscar'}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <MapLocationPicker
              latitude={latNum}
              longitude={lngNum}
              areaGeojson={formData.area_geojson}
              editable
              height="260px"
              onChange={({ latitude: la, longitude: lo }) => {
                setFormData((f) => ({
                  ...f,
                  latitude: String(la),
                  longitude: String(lo),
                }));
              }}
            />
            <div className="form-stack">
              <div className="form-group mb-0">
                <label htmlFor="ent-lat" className="form-label">
                  Latitude
                </label>
                <input
                  id="ent-lat"
                  type="text"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="-23.5505"
                />
              </div>
              <div className="form-group mb-0">
                <label htmlFor="ent-lng" className="form-label">
                  Longitude
                </label>
                <input
                  id="ent-lng"
                  type="text"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="-46.6333"
                />
              </div>
            </div>
            <div className="form-group mb-0">
              <label htmlFor="ent-area" className="form-label">
                Polígono (GeoJSON)
              </label>
              <textarea
                id="ent-area"
                name="area_geojson"
                rows={4}
                value={formData.area_geojson || ''}
                onChange={handleChange}
                className="form-input font-mono text-xs"
                placeholder='{"type":"Polygon","coordinates":[[[...]]]}'
              />
              <button
                type="button"
                className="btn btn-outline btn-sm mt-2"
                onClick={() => {
                  const lat = formData.latitude === '' ? null : Number(formData.latitude);
                  const lng = formData.longitude === '' ? null : Number(formData.longitude);
                  if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
                    toast.error('Informe latitude e longitude para gerar o buffer.');
                    return;
                  }
                  setFormData((prev) => ({
                    ...prev,
                    area_geojson: stringifyGeoJSON(bufferPointGeoJSON(lat, lng, 50)),
                  }));
                }}
              >
                Gerar polígono de 50 m em torno do ponto
              </button>
            </div>
          </div>
        </div>

        {enterpriseId ? (
          <div className="rounded-lg border border-gray-100 bg-slate-50/80 p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                <ImageIcon className="h-4 w-4 text-primary-600" aria-hidden />
                Galeria do empreendimento
              </div>
              <Can any={[P.IDENTITY_ACCESS, P.ENTERPRISES_VIEW]}>
                <label className="btn btn-outline btn-sm inline-flex cursor-pointer items-center gap-2">
                  {uploadFotoMutation.isLoading ? 'Enviando…' : 'Adicionar foto'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                    className="hidden"
                    disabled={uploadFotoMutation.isLoading}
                    onChange={handleFotoInput}
                  />
                </label>
              </Can>
            </div>
            <p className="mb-3 text-xs text-gray-600">JPG, PNG ou WEBP (máx. 15 MB por arquivo).</p>
            {fotosLoading ? (
              <p className="text-sm text-gray-600">Carregando fotos…</p>
            ) : fotos.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhuma foto cadastrada.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {fotos.map((ph) => (
                  <div
                    key={ph.id}
                    className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
                  >
                    <AuthenticatedBlobImage
                      path={ph.url_path || `/api/v1/identity/enterprise-fotos/${ph.id}/file`}
                      alt={ph.caption || `Foto ${ph.ordinal ?? ''}`}
                      className="aspect-square w-full object-cover"
                    />
                    {ph.caption ? (
                      <p className="line-clamp-2 border-t border-gray-100 px-2 py-1 text-xs text-gray-600">
                        {ph.caption}
                      </p>
                    ) : null}
                    <Can any={[P.IDENTITY_ACCESS, P.ENTERPRISES_VIEW]}>
                      <button
                        type="button"
                        className="absolute right-1 top-1 rounded bg-white/90 px-2 py-0.5 text-xs text-red-700 opacity-0 shadow transition group-hover:opacity-100"
                        disabled={deleteFotoMutation.isLoading}
                        onClick={() => {
                          if (window.confirm('Remover esta foto?')) deleteFotoMutation.mutate(ph.id);
                        }}
                      >
                        Remover
                      </button>
                    </Can>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </form>
    </FormModalLayout>
  );
};

export default EnterpriseModal;
